/**
 * AiScenarioBuilder — Now uses the Dignity chat experience.
 *
 * 2-step flow:
 * 1. DignityConversation (intake + payment + AI chat in one thread)
 * 2. Review & Save (unchanged)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, Share2, CheckCircle2 } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { scenarioStorage } from '@/lib/scenarioStorage';
import { FamilyMemberPills } from '@/components/family/FamilyMemberPills';
import { toast } from 'sonner';
import { DignityConversation } from './DignityConversation';
import { DignityAvatar } from './DignityAvatar';
import { ScenarioReviewPanel } from './ScenarioReviewPanel';
import { ScenarioVisibilityToggle } from './ScenarioVisibilityToggle';
import type { FinalizedScenario } from '@/hooks/useDignityChat';

interface AiScenarioBuilderProps {
  onBack: () => void;
  onCreated?: (scenarioId: string) => void;
  onClose: () => void;
  onStepChange?: (step: number) => void;
}

export function AiScenarioBuilder({ onBack, onCreated, onClose, onStepChange }: AiScenarioBuilderProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const [step, setStep] = useState<'chat' | 'review'>('chat');
  const [finalizedScenario, setFinalizedScenario] = useState<FinalizedScenario | null>(null);
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('unlisted');
  const [isSaving, setIsSaving] = useState(false);
  const [familyMemberId, setFamilyMemberId] = useState<string | null>(null);

  const handleFinalized = (scenario: FinalizedScenario) => {
    setFinalizedScenario(scenario);
    setStep('review');
    onStepChange?.(2);
  };

  const handleSave = async (publish: boolean) => {
    if (!finalizedScenario) return;

    setIsSaving(true);
    try {
      const phases = Array.from(new Set(finalizedScenario.procedures.map(p => p.phase).filter(Boolean)));
      const newScenario = scenarioStorage.saveScenario({
        name: finalizedScenario.name,
        description: finalizedScenario.description,
        duration: finalizedScenario.duration,
        totalProcedures: finalizedScenario.procedures.length,
        estimatedCost: finalizedScenario.procedures.reduce((s, p) => s + p.estimatedCost * p.quantity, 0),
        procedures: finalizedScenario.procedures,
        phases: phases.length > 0 ? phases : ['Treatment'],
        tags: finalizedScenario.tags,
        severityLevel: finalizedScenario.severityLevel,
        isDraft: false,
        visibility,
        shareToken: '',
        source: 'ai_generated',
        ...(familyMemberId ? { familyMemberId } : {}),
      });

      const shareUrl = scenarioStorage.getShareUrl(newScenario);

      if (publish) {
        toast.success('AI Scenario saved & shared!', {
          description: `Share link: ${shareUrl}`,
          duration: 6000,
        });
      } else {
        toast.success('AI Scenario saved locally!');
      }

      if (onCreated) onCreated(newScenario.id);
      onClose();
    } catch (error) {
      console.error('Error saving AI scenario:', error);
      toast.error('Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const previewSlug = finalizedScenario?.name
    ? finalizedScenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    : 'ai-scenario';
  const previewShareUrl = `${window.location.origin}/scenarios/preview/${previewSlug}`;

  return (
    <>
      {/* Step 1: Dignity Conversation (intake + payment + AI chat) */}
      {step === 'chat' && (
        <>
          {/* Header with Dignity avatar */}
          <div className="flex items-center gap-3 mb-4">
            <DignityAvatar size="chat" />
            <div>
              <h2 className={`text-lg font-bold ${
                ahpDark ? 'text-[#f0ebe3]' : isAhp ? 'text-gray-900' : 'text-foreground'
              }`}>
                Chat with Dignity
              </h2>
              <p className="text-xs text-muted-foreground">
                Your financial healthcare advisor
              </p>
            </div>
          </div>

          <DignityConversation onFinalized={handleFinalized} />

          <div className="flex justify-between items-center mt-4 pt-3 border-t border-foreground/5">
            <Button variant="outline" onClick={onBack} size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div />
          </div>
        </>
      )}

      {/* Step 2: Review */}
      {step === 'review' && finalizedScenario && (
        <div className="space-y-5 py-4">
          <div className={`rounded-xl p-4 ${
            ahpDark
              ? 'bg-gradient-to-r from-[#1a2a40] to-[#0f1d2f] border border-[rgba(197,165,90,0.2)]'
              : isAhp
              ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
              : 'bg-gradient-to-r from-amber-500/10 to-amber-500/5 border border-amber-500/20'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className={`w-5 h-5 ${isAhp ? 'text-green-600' : 'text-green-400'}`} />
              <h3 className={`text-sm font-bold ${
                ahpDark ? 'text-[#f0ebe3]' : isAhp ? 'text-gray-900' : 'text-foreground'
              }`}>
                AI Analysis Complete
              </h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {finalizedScenario.aiConsumerMessage}
            </p>
          </div>

          <ScenarioReviewPanel
            name={finalizedScenario.name}
            description={finalizedScenario.description}
            duration={finalizedScenario.duration}
            severityLevel={finalizedScenario.severityLevel}
            tags={finalizedScenario.tags}
            procedures={finalizedScenario.procedures}
            iconColor="#f59e0b"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {finalizedScenario.aiClinicalSummary && (
              <div className={`rounded-lg p-3 ${
                ahpDark ? 'bg-blue-900/20 border border-blue-500/20' : isAhp ? 'bg-blue-50 border border-blue-200' : 'bg-blue-500/5 border border-blue-500/20'
              }`}>
                <h4 className={`text-xs font-bold mb-1 ${
                  ahpDark ? 'text-blue-300' : isAhp ? 'text-blue-800' : 'text-blue-400'
                }`}>Clinical Summary</h4>
                <p className="text-[11px] text-muted-foreground">{finalizedScenario.aiClinicalSummary}</p>
              </div>
            )}
            {finalizedScenario.aiCostAnalysis && (
              <div className={`rounded-lg p-3 ${
                ahpDark ? 'bg-green-900/20 border border-green-500/20' : isAhp ? 'bg-green-50 border border-green-200' : 'bg-green-500/5 border border-green-500/20'
              }`}>
                <h4 className={`text-xs font-bold mb-1 ${
                  ahpDark ? 'text-green-300' : isAhp ? 'text-green-800' : 'text-green-400'
                }`}>Cost Analysis</h4>
                <p className="text-[11px] text-muted-foreground">{finalizedScenario.aiCostAnalysis}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h4 className={`text-sm font-medium ${
              ahpDark ? 'text-[#f0ebe3]' : isAhp ? 'text-gray-900' : 'text-foreground'
            }`}>
              Who is this for? <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
            </h4>
            <FamilyMemberPills
              selectedMemberId={familyMemberId}
              onSelect={setFamilyMemberId}
            />
          </div>

          <ScenarioVisibilityToggle
            visibility={visibility}
            onVisibilityChange={setVisibility}
            shareUrl={previewShareUrl}
          />

          <div className="flex justify-between items-center mt-6 pt-4 border-t border-foreground/5">
            <Button variant="outline" onClick={() => { setStep('chat'); onStepChange?.(1); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Chat
            </Button>

            <div className="flex gap-2">
              <Button
                onClick={() => handleSave(false)}
                variant="outline"
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Locally'}
              </Button>

              <Button
                onClick={() => handleSave(true)}
                className={isAhp ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-black'}
                disabled={isSaving}
              >
                <Share2 className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save & Share'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
